import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import { db } from '../utils/firebase';
import useBreweryBeers from './useBreweryBeers';
import useBreweryReviewSeenSet from './useBreweryReviewSeenSet';
import {
  aggregateReviewInboxItems,
} from '../utils/reviewDetails/aggregateReviewInboxItems';
import { REVIEW_DETAILS_INBOX_QUERY_LIMIT } from '../utils/reviewDetails/reviewDetailsConstants';

/**
 * @param {import('firebase/firestore').Timestamp | Date | string | number | undefined} dateValue
 * @returns {Date}
 */
function normalizeReviewDate(dateValue) {
  if (dateValue && typeof dateValue.toDate === 'function') {
    return dateValue.toDate();
  }
  if (dateValue) {
    return new Date(dateValue);
  }
  return new Date();
}

/**
 * @param {number | null | undefined} value
 * @returns {number | null}
 */
function resolveHospitalityAverageRating(hospitalityRatings) {
  const qualityRating = hospitalityRatings?.qualityRating;
  if (typeof qualityRating?.average === 'number' && Number.isFinite(qualityRating.average)) {
    return qualityRating.average;
  }
  if (typeof qualityRating?.rating === 'number' && Number.isFinite(qualityRating.rating)) {
    return qualityRating.rating;
  }
  return null;
}

/**
 * @param {object} params
 * @param {string | null | undefined} params.breweryId
 * @param {string} [params.breweryName]
 * @returns {{ items: import('../utils/reviewDetails/aggregateReviewInboxItems').ReviewDetailsInboxItem[], loading: boolean, error: string | null }}
 */
function useBreweryReviewInbox({ breweryId, breweryName = '' }) {
  const { beers, loading: beersLoading } = useBreweryBeers(breweryId);
  const { seenKeys, loading: seenLoading, error: seenError } = useBreweryReviewSeenSet(breweryId);
  const [rawInbox, setRawInbox] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchInbox() {
      if (typeof breweryId !== 'string' || breweryId.length === 0) {
        if (!cancelled) {
          setRawInbox(null);
          setFetchLoading(false);
          setError(null);
        }
        return;
      }

      if (beersLoading) {
        return;
      }

      try {
        if (!cancelled) {
          setFetchLoading(true);
          setError(null);
        }

        let resolvedBreweryName = breweryName;
        let hospitalityRating = null;
        try {
          const breweryDoc = await getDoc(doc(db, 'breweries', breweryId));
          if (breweryDoc.exists()) {
            const breweryData = breweryDoc.data();
            if (typeof breweryData.name === 'string' && breweryData.name.length > 0) {
              resolvedBreweryName = breweryData.name;
            }
            hospitalityRating = resolveHospitalityAverageRating(breweryData.hospitalityRatings);
          }
        } catch (breweryFetchError) {
          console.error(`Error fetching brewery ${breweryId}:`, breweryFetchError);
        }

        const beerReviewsRef = collection(db, 'beerReview');
        const beerReviewsQuery = query(
          beerReviewsRef,
          where('breweryId', '==', breweryId),
          orderBy('date', 'desc'),
          limit(REVIEW_DETAILS_INBOX_QUERY_LIMIT),
        );
        const beerReviewsSnapshot = await getDocs(beerReviewsQuery);

        const hospitalityReviewsRef = collection(db, 'hospitalityReview');
        const hospitalityReviewsQuery = query(
          hospitalityReviewsRef,
          where('breweryId', '==', breweryId),
          orderBy('date', 'desc'),
          limit(REVIEW_DETAILS_INBOX_QUERY_LIMIT),
        );
        const hospitalityReviewsSnapshot = await getDocs(hospitalityReviewsQuery);

        if (cancelled) {
          return;
        }

        const beerReviews = beerReviewsSnapshot.docs.map((reviewDoc) => {
          const reviewData = reviewDoc.data();
          return {
            reviewType: 'beer',
            reviewId: reviewDoc.id,
            beerId: typeof reviewData.beerId === 'string' ? reviewData.beerId : '',
            date: normalizeReviewDate(reviewData.date),
          };
        });

        const hospitalityReviews = hospitalityReviewsSnapshot.docs.map((reviewDoc) => ({
          reviewType: 'hospitality',
          reviewId: reviewDoc.id,
          date: normalizeReviewDate(reviewDoc.data().date),
        }));

        setRawInbox({
          breweryId,
          breweryName: resolvedBreweryName.length > 0 ? resolvedBreweryName : breweryId,
          breweryHospitalityRating: hospitalityRating,
          beerReviews,
          hospitalityReviews,
        });
      } catch (fetchError) {
        console.error('Error fetching brewery review inbox:', fetchError);
        if (!cancelled) {
          setError(fetchError.message);
          setRawInbox(null);
        }
      } finally {
        if (!cancelled) {
          setFetchLoading(false);
        }
      }
    }

    fetchInbox();

    return () => {
      cancelled = true;
    };
  }, [breweryId, breweryName, beersLoading]);

  const items = useMemo(() => {
    if (!rawInbox || beersLoading || seenLoading) {
      return [];
    }

    return aggregateReviewInboxItems({
      breweryId: rawInbox.breweryId,
      breweryName: rawInbox.breweryName,
      breweryHospitalityRating: rawInbox.breweryHospitalityRating,
      beerCatalog: beers,
      beerReviews: rawInbox.beerReviews,
      hospitalityReviews: rawInbox.hospitalityReviews,
      seenKeys,
    });
  }, [beers, beersLoading, rawInbox, seenKeys, seenLoading]);

  const breweryHospitalityRating = rawInbox?.breweryHospitalityRating ?? null;
  const combinedLoading = fetchLoading || beersLoading || seenLoading;
  const combinedError = error || seenError;

  return {
    items,
    breweryHospitalityRating,
    loading: combinedLoading,
    error: combinedError,
  };
}

export default useBreweryReviewInbox;
